import { FieldType, IFilter } from "@saleor/components/Filter";
import { maybe } from "@saleor/misc";
import { FilterOpts, MinMax } from "@saleor/types";
import {
  RiderDispatchBatchFilterInput,
  StaffNameInput
} from "@saleor/types/globalTypes";
import {
  createDateField,
  createMultipleTextField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

import { IFilterElement } from "../../../components/Filter";
import {
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleTextFieldQueryParam as getMultiText
} from "../../../utils/filters";
import { DispatchHistoryUrlFilters } from "../../url";

export enum DispatchHistoryFilterKeys {
  riderName = "riderName",
  riderFirstName = "riderFirstName",
  riderLastName = "riderLastName",
  deliveryDateRange = "deliveryDateRange",
  dateDispatched = "dateDispatched",
  dateDispatchedFrom = "dateDispatchedFrom",
  dateDispatchedTo = "dateDispatchedTo",
  deliveryDateRangeFrom = "deliveryDateRangeFrom",
  deliveryDateRangeTo = "deliveryDateRangeTo"
}

export interface DispatchHistoryListFilterOpts {
  deliveryDateRange: FilterOpts<MinMax>;
  riderName: FilterOpts<StaffNameInput>;
  // deliveryStatus: FilterOpts<DispatchHistoryStatusEnum[]>;
  dateDispatched: FilterOpts<MinMax>;
}

const messages = defineMessages({
  dateDispatched: {
    defaultMessage: "Date Dispatched",
    description: "date dispatched range"
  },
  deliveryDateRange: {
    defaultMessage: "Delivery Date Range",
    description: "delivery date range"
  },
  dispatchHistoryStatusActive: {
    defaultMessage: "Active",
    description: "active"
  },
  dispatchHistoryStatusInactive: {
    defaultMessage: "Inactive",
    description: "inactive"
  },
  riderName: {
    defaultMessage: "Rider Name",
    description: "rider name"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: DispatchHistoryListFilterOpts
): IFilter<DispatchHistoryFilterKeys> {
  return [
    {
      ...createMultipleTextField(
        DispatchHistoryFilterKeys.riderName,
        "Rider Name",
        [
          {
            active: opts.riderName.active,
            label: "First name",
            multiple: false,
            name: DispatchHistoryFilterKeys.riderFirstName,
            type: FieldType.text,
            value: opts.riderName.value.firstName
          },
          {
            active: opts.riderName.active,
            label: "Last name",
            multiple: false,
            name: DispatchHistoryFilterKeys.riderLastName,
            type: FieldType.text,
            value: opts.riderName.value.lastName
          }
        ]
      ),
      active: opts.riderName.active
    },

    {
      ...createDateField(
        DispatchHistoryFilterKeys.deliveryDateRange,
        intl.formatMessage(messages.deliveryDateRange),
        opts.deliveryDateRange.value
      ),
      active: opts.deliveryDateRange.active
    },

    {
      ...createDateField(
        DispatchHistoryFilterKeys.dateDispatched,
        intl.formatMessage(messages.dateDispatched),
        opts.dateDispatched.value
      ),
      active: opts.dateDispatched.active
    }
  ];
}

export interface DispatchHistoryFilterOpts {
  riderName: FilterOpts<StaffNameInput>;
  deliveryDateRange: FilterOpts<MinMax>;
  // deliveryStatus: FilterOpts<DispatchHistoryStatusEnum[]>;
  dateDispatched: FilterOpts<MinMax>;
}

export function getFilterOpts(
  params: DispatchHistoryUrlFilters
): DispatchHistoryFilterOpts {
  return {
    dateDispatched: {
      active: maybe(
        () =>
          [params.dateDispatchedFrom, params.dateDispatchedTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.dateDispatchedFrom, ""),
        min: maybe(() => params.dateDispatchedTo, "")
      }
    },
    deliveryDateRange: {
      active: maybe(
        () =>
          [params.deliveryDateRangeFrom, params.deliveryDateRangeTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.deliveryDateRangeTo, ""),
        min: maybe(() => params.deliveryDateRangeFrom, "")
      }
    },
    //   deliveryStatus: {
    //     active: maybe(() => params.verticalType !== undefined, false),
    //     value: maybe(
    //       () =>
    //         dedupeFilter(
    //           params.verticalType.map(status =>
    //             findValueInEnum(status, DispatchHistoryStatusEnum)
    //           )
    //         ),
    //       []
    //     )
    //   },
    riderName: {
      active:
        !!maybe(() => params.riderFirstName) ||
        !!maybe(() => params.riderLastName),
      value: {
        firstName: params.riderFirstName,
        lastName: params.riderLastName
      }
    }
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<DispatchHistoryFilterKeys>
): DispatchHistoryUrlFilters {
  const { name } = filter;
  switch (name) {
    case DispatchHistoryFilterKeys.dateDispatched:
      return getMinMaxQueryParam(
        filter,
        DispatchHistoryFilterKeys.dateDispatchedFrom,
        DispatchHistoryFilterKeys.dateDispatchedTo
      );

    case DispatchHistoryFilterKeys.deliveryDateRange:
      return getMinMaxQueryParam(
        filter,
        DispatchHistoryFilterKeys.deliveryDateRangeFrom,
        DispatchHistoryFilterKeys.deliveryDateRangeTo
      );

    case DispatchHistoryFilterKeys.riderName:
      return {
        riderFirstName: filter.active
          ? getMultiText(filter, DispatchHistoryFilterKeys.riderLastName) &&
            getMultiText(filter, DispatchHistoryFilterKeys.riderFirstName) ===
              undefined
            ? ""
            : getMultiText(filter, DispatchHistoryFilterKeys.riderFirstName)
          : undefined,

        riderLastName: filter.active
          ? getMultiText(filter, DispatchHistoryFilterKeys.riderFirstName) &&
            getMultiText(filter, DispatchHistoryFilterKeys.riderLastName) ===
              undefined
            ? ""
            : getMultiText(filter, DispatchHistoryFilterKeys.riderLastName)
          : undefined
      };
  }
}

export function getFilterVariables(
  params: DispatchHistoryUrlFilters
): RiderDispatchBatchFilterInput {
  return {
    batchCode: params.batchCode,
    created: getGteLteVariables({
      gte: params.dateDispatchedFrom,
      lte: params.dateDispatchedTo
    }),
    deliveryDate: getGteLteVariables({
      gte: params.deliveryDateRangeFrom,
      lte: params.deliveryDateRangeTo
    }),
    // deliveryTypes: maybe(() =>
    //   params.deliveryType.map(status => findInEnum(status, DeliveryTypeEnum))
    // ),
    // paymentMethod:
    //   params.payment !== undefined
    //     ? findValueInEnum(params.payment, PaymentType)
    //     : null,
    riderName: {
      firstName: params.riderFirstName,
      lastName: params.riderLastName
    }
  };
}
